import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Chip,
  Divider,
  IconButton,
  Tabs,
  Tab,
  Rating,
  Button,
  Snackbar,
  Alert
} from '@mui/material';
import { ArrowBack, Code, Chat, Assessment, PlayArrow, Star, ContentCopy, Fullscreen, FullscreenExit } from '@mui/icons-material';
import { getRrwebEventsBySessionAndUser } from '../../service/rrwebService';
import { getSessionById } from '../../service/sessionService';
import { getGuiSessionConfig } from '../workspace/config/guiSessionConfig';
import ReplayWorkspace from '../workspaceReplay2/ReplayWorkspace';
import ReplayControls from '../workspaceReplay2/components/ReplayControls';
import { usePlayback } from '../workspaceReplay2/hooks/usePlayback';
import { compressIdleGaps } from '../workspaceReplay2/utils/compressIdleGaps';
import { changeSteps } from '../workspaceReplay2/utils/replayChanges';
import { autoSegmentEvents } from '../workspaceReplay2/utils/autoSegmentation';

// The old "Session Replay" tab (SessionReplayView, now removed) rendered rrweb-player
// against getRrwebEventsBySessionAndUser - the rrweb_events table stopped being written on
// 2026-08-21/22 when the app moved to batched telemetry_events, AND rrweb's DOM-mutation
// snapshot format was never ported to the new pipeline at all, so that tab could never show
// data for any current session regardless of the query. This renders the same telemetry-based
// scrubbing replay already used in Monitor > Replay (see presenterTab.js) instead - it trades
// true pixel-perfect DOM playback for something that actually has data.
// replayOptions (RawDataPanel.jsx, for pages replayed through an adapter such as Reasoning
// Gap) sets the replay's task, tests and extra panels; null keeps the workspace replay. Also
// the experiment Overview's student dialog (StudentReplay.jsx), whose replayOptions.formatText
// shows the student's words through its word filter.
export function TelemetryReplayView({ events, isGuiSession, guiConfig, replayOptions }) {
  const replayData = useMemo(() => {
    const sorted = (events || [])
      .filter(e => e.timestamp)
      .sort((a, b) => a.timestamp - b.timestamp);
    if (sorted.length === 0) return null;
    const filtered = replayOptions?.idleGapMs ? compressIdleGaps(sorted, replayOptions.idleGapMs) : sorted;
    const startTime = filtered[0].timestamp;
    const endTime = filtered[filtered.length - 1].timestamp;
    const duration = endTime - startTime;
    const segments = autoSegmentEvents(filtered, startTime);
    const changeTimes = replayOptions?.stepEdits ? changeSteps(filtered, replayOptions.starterCode).map(step => step.timestamp - startTime) : [];
    return { events: filtered, startTime, duration, segments, changeTimes };
  }, [events, replayOptions]);

  const playback = usePlayback(replayData?.duration || 0);
  const [expanded, setExpanded] = useState(!!replayOptions?.startExpanded);
  // The tab can open before the student's data (and with it replayOptions) has loaded.
  const startExpanded = !!replayOptions?.startExpanded;
  useEffect(() => {
    if (startExpanded) setExpanded(true);
  }, [startExpanded]);

  // In full screen, Escape leaves full screen instead of closing the whole dialog. (With
  // nothing to replay the full-screen view isn't shown, so Escape closes the dialog as usual.)
  const showingFullScreen = expanded && !!replayData;
  useEffect(() => {
    if (!showingFullScreen) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setExpanded(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [showingFullScreen]);

  const formatPlaybackTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  if (!replayData) {
    return (
      <Box sx={{ height: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No session activity recorded for this student yet.
        </Typography>
      </Box>
    );
  }

  const { changeTimes } = replayData;
  const changesSoFar = changeTimes.filter(t => t <= playback.currentTime).length;
  const previousChangeTime = changesSoFar >= 2 ? changeTimes[changesSoFar - 2] : 0;
  const stepTo = (time) => {
    playback.pause();
    playback.seek(time);
  };
  const toggleLabel = expanded ? 'Exit full screen' : 'Full screen';
  const toggleIcon = expanded ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />;

  return (
    <Box sx={expanded
      ? { position: 'fixed', inset: 0, zIndex: 1500, display: 'flex', flexDirection: 'column', backgroundColor: '#1f2937' }
      : { height: '600px', display: 'flex', flexDirection: 'column', backgroundColor: '#1f2937', borderRadius: 1, overflow: 'hidden', position: 'relative' }}>
      {replayOptions?.stepEdits ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 0.75, color: '#e5e7eb', borderBottom: '1px solid #374151', flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" color="inherit" disabled={changesSoFar === 0 || previousChangeTime === playback.currentTime} onClick={() => stepTo(previousChangeTime)}>
            ◀ Previous change
          </Button>
          <Typography variant="body2" sx={{ minWidth: 130, textAlign: 'center' }}>
            Change {changesSoFar} of {changeTimes.length}
          </Typography>
          <Button size="small" variant="outlined" color="inherit" disabled={changesSoFar >= changeTimes.length} onClick={() => stepTo(changeTimes[changesSoFar])}>
            Next change ▶
          </Button>
          {replayOptions.stepNote && (
            <Typography variant="caption" sx={{ color: '#9ca3af', flex: 1, minWidth: 200 }}>{replayOptions.stepNote}</Typography>
          )}
          <Button size="small" color="inherit" startIcon={toggleIcon} onClick={() => setExpanded(v => !v)} sx={{ ml: 'auto' }}>
            {toggleLabel}
          </Button>
        </Box>
      ) : (
        <Button size="small" startIcon={toggleIcon} onClick={() => setExpanded(v => !v)}
          sx={{ position: 'absolute', top: 14, right: 16, zIndex: 20, color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.2)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.35)' } }}>
          {toggleLabel}
        </Button>
      )}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
        <ReplayWorkspace
          playback={playback}
          events={replayData.events}
          startTime={replayData.startTime}
          task={replayOptions ? replayOptions.task : (guiConfig?.assignment || '')}
          starterCode={replayOptions ? replayOptions.starterCode : (guiConfig?.starterCode || '')}
          isGuiSession={isGuiSession}
          guiConfig={guiConfig}
          {...(replayOptions && {
            testCases: replayOptions.testCases,
            testCasesTitle: replayOptions.testCasesTitle,
            explanationPrompt: replayOptions.explanationPrompt,
            runLabel: replayOptions.runLabel,
            chatWelcome: replayOptions.chatWelcome,
            showMouse: replayOptions.showMouse,
            initialSizes: replayOptions.initialSizes,
            showNavbar: replayOptions.showNavbar,
            showChanges: replayOptions.showChanges,
            formatText: replayOptions.formatText,
          })}
        />
      </Box>
      <ReplayControls
        currentTime={playback.currentTime}
        duration={playback.duration}
        isPlaying={playback.isPlaying}
        playbackSpeed={playback.playbackSpeed}
        onTogglePlay={playback.togglePlay}
        onSeek={playback.seek}
        onReset={playback.reset}
        onCycleSpeed={playback.cycleSpeed}
        taggedBlocks={replayData.segments.filter(s => !s.isSystemEvent)}
        showLabeling={false}
        activeRecording={null}
        isAutoMode={true}
        autoSegments={replayData.segments}
        onAssignBehavior={() => {}}
        formatTime={formatPlaybackTime}
        viewOnly={true}
        events={replayData.events}
        startTime={replayData.startTime}
        markersWithoutLabeling={!!replayOptions?.timelineMarkers}
      />
    </Box>
  );
}

const StudentDetailView = ({ student, students, studentData, sessionId, isGuiSession, replayOptions = null, onBack }) => {
  const [detailTab, setDetailTab] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  // Only needed for the Session Replay tab (below) to render the right layout for
  // InitialFinal/InitialFinalCSS - this component previously had no access to the session's
  // test_code/type at all, so replay always fell back to the generic GUI template regardless
  // of the actual session type. Fetched once here rather than threaded down as a prop, since
  // none of this component's other tabs need it.
  const [guiConfig, setGuiConfig] = useState(null);
  useEffect(() => {
    if (!isGuiSession || !sessionId) return;
    let cancelled = false;
    getSessionById(sessionId).then((session) => {
      if (cancelled || !session) return;
      setGuiConfig(getGuiSessionConfig(session));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [sessionId, isGuiSession]);

  const getStudentInitials = (student) => {
    const first = student.first_name?.[0] || '';
    const last = student.last_name?.[0] || '';
    return `${first}${last}`.toUpperCase();
  };

  const getStudentColor = (index) => {
    const colors = ['#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2', '#0097a7'];
    return colors[index % colors.length];
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // Lightweight copy - just timeline data, no rrweb
  const handleCopyTimelineOnly = () => {
    const exportData = {
      student: {
        id: student.id,
        first_name: student.first_name,
        last_name: student.last_name,
        email: student.email,
        session_id: sessionId
      },
      summary: {
        total_interactions: studentData.interactions?.length || 0,
        total_messages: studentData.messages?.length || 0,
        total_test_runs: studentData.testRuns?.length || 0,
        total_code_submissions: studentData.codeSubmissions?.length || 0,
        total_terminal_outputs: studentData.terminalOutput?.length || 0,
        total_events: studentData.allEvents?.length || 0
      },
      chronological_timeline: studentData.allEvents?.map(e => ({
        type: e.type,
        timestamp: e.timestamp,
        ...(e.type === 'interaction' && {
          question_type: e.question_type,
          student_intent: e.student_intent,
          student_facing_description: e.student_facing_description,
          student_reported_intent: e.student_reported_intent,
          intent_rating: e.intent_rating,
          rating_timestamp: e.rating_timestamp,
          message_history: e.message_history
        }),
        ...(e.type === 'message' && {
          sender_id: e.sender_id,
          content: e.content,
          is_student: e.sender_id !== -4
        }),
        ...(e.type === 'test_run' && {
          result_list: e.result_list,
          error: e.error
        }),
        ...(e.type === 'code_submission' && {
          content: e.content,
          passrate: e.passrate
        }),
        ...(e.type === 'terminal_output' && {
          text: e.text
        })
      })) || []
    };

    const jsonString = JSON.stringify(exportData, null, 2);
    
    navigator.clipboard.writeText(jsonString).then(() => {
      setCopyMessage('Timeline data copied!');
      setCopySuccess(true);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // Full copy - includes rrweb data (mouse movements, etc.)
  const handleCopyAllData = async () => {
    let terminalOutput = studentData.terminalOutput || [];
    let mouseData = [];
    
    try {
      const rrwebBatches = await getRrwebEventsBySessionAndUser(sessionId, student.id);
      
      if (rrwebBatches && Array.isArray(rrwebBatches)) {
        const allRrwebEvents = rrwebBatches;
        
        // Extract terminal output if not already present
        if (!studentData.terminalOutput || studentData.terminalOutput.length === 0) {
          terminalOutput = allRrwebEvents
            .filter(e => e.type === 3 && e.data?.source === 0 && e.data?.texts)
            .flatMap(e => 
              e.data.texts
                .filter(t => t.value && t.value.trim().length > 0)
                .map(t => ({
                  timestamp: e.timestamp,
                  node_id: t.id,
                  text: t.value
                }))
            )
            .filter(t => {
              const text = t.text.trim();
              const isError = text.includes('Error') || 
                              text.includes('Traceback') || 
                              text.includes('File "<exec>"') ||
                              text.includes('passed');
              const isNoise = text === 'Run Tests' || 
                              text === 'Running...' || 
                              text.length < 5;
              return isError && !isNoise;
            });
        }
        
        // Extract mouse movements and clicks
        mouseData = allRrwebEvents
          .filter(event => {
            if (event.type === 3) {
              const source = parseInt(event.data?.source);
              return source === 1 || source === 2;
            }
            return false;
          })
          .map(event => ({
            timestamp: new Date(event.timestamp).toISOString(), 
            type: parseInt(event.data?.source) === 1 ? 'mouse_move' : 'mouse_click',
            ...(parseInt(event.data?.source) === 1 && {
              positions: event.data?.positions
            }),
            ...(parseInt(event.data?.source) === 2 && {
              x: event.data?.x,
              y: event.data?.y,
              click_type: event.data?.type
            })
          }));
      }
    } catch (err) {
      console.error('Error fetching rrweb data:', err);
    }

    const exportData = {
      student: {
        id: student.id,
        first_name: student.first_name,
        last_name: student.last_name,
        email: student.email,
        session_id: sessionId
      },
      summary: {
        total_interactions: studentData.interactions?.length || 0,
        total_messages: studentData.messages?.length || 0,
        total_test_runs: studentData.testRuns?.length || 0,
        total_code_submissions: studentData.codeSubmissions?.length || 0,
        total_terminal_outputs: terminalOutput.length,
        mouse_movements: mouseData.filter(m => m.type === 'mouse_move').length,
        mouse_clicks: mouseData.filter(m => m.type === 'mouse_click').length,
        total_events: studentData.allEvents?.length || 0
      },
      interactions: studentData.interactions?.map(i => ({
        id: i.id,
        timestamp: i.created_time,  
        question_type: i.question_type,
        student_intent: i.student_intent,
        student_facing_description: i.student_facing_description,
        student_reported_intent: i.student_reported_intent,
        intent_rating: i.intent_rating,
        rating_timestamp: i.rating_timestamp,
        code_snapshot: i.code_snapshot,
        message_history: i.message_history
      })) || [],
      messages: studentData.messages?.map(m => ({
        id: m.id,
        sender_id: m.sender_id,
        recipient_id: m.recipient_id,
        content: m.content,
        timestamp: m.created_time,  
        keystrokes: m.keystrokes,
        question_type: m.question_type
      })) || [],
      test_runs: studentData.testRuns?.map(t => ({
        id: t.id,
        timestamp: t.created_time,  
        result_list: t.result_list,
        error: t.error
      })) || [],
      code_submissions: studentData.codeSubmissions?.map(c => ({
        id: c.id,
        timestamp: c.created_time,
        content: c.content,
        passrate: c.passrate,
        keystrokes: c.keystrokes?.map(k => {
          const { time, ...rest } = k;  
          return {
            ...rest,
            timestamp: time  
          };
        })
      })) || [],
      terminal_output: terminalOutput,
      mouse_activity: mouseData,
      chronological_timeline: studentData.allEvents?.map(e => ({
        type: e.type,
        timestamp: e.timestamp,
        ...(e.type === 'interaction' && {
          question_type: e.question_type,
          student_intent: e.student_intent,
          student_facing_description: e.student_facing_description,
          student_reported_intent: e.student_reported_intent,
          intent_rating: e.intent_rating,
          rating_timestamp: e.rating_timestamp,
          code_snapshot: e.code_snapshot,
          message_history: e.message_history
        }),
        ...(e.type === 'message' && {
          sender_id: e.sender_id,
          content: e.content,
          keystrokes: e.keystrokes
        }),
        ...(e.type === 'test_run' && {
          result_list: e.result_list,
          error: e.error
        }),
        ...(e.type === 'code_submission' && {
          content: e.content,
          passrate: e.passrate,
          keystrokes: e.keystrokes
        }),
        ...(e.type === 'terminal_output' && {
          text: e.text,
          node_id: e.node_id
        })
      })) || []
    };

    const jsonString = JSON.stringify(exportData, null, 2);
    
    navigator.clipboard.writeText(jsonString).then(() => {
      setCopyMessage('All data copied (including mouse activity)!');
      setCopySuccess(true);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // Render chronological timeline
  const renderChronologicalView = () => (
    <Box sx={{ height: '600px', overflowY: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Chronological Activity Timeline
      </Typography>
      
      {studentData.allEvents?.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No activity recorded for this student yet.
        </Typography>
      ) : (
        studentData.allEvents?.map((event, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2, borderLeft: `4px solid ${
            event.type === 'interaction' ? '#1976d2' : 
            event.type === 'message' ? '#388e3c' : 
            event.type === 'test_run' ? '#9c27b0' :
            event.type === 'terminal_output' ? '#ff6f00' :
            '#f57c00'
          }` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Chip 
                label={
                  event.type === 'interaction' ? 'LLM INTERACTION' : 
                  event.type === 'terminal_output' ? 'TERMINAL OUTPUT' :
                  event.type.toUpperCase().replace('_', ' ')
                }
                size="small"
                color={
                  event.type === 'interaction' ? 'primary' : 
                  event.type === 'message' ? 'success' : 
                  event.type === 'test_run' ? 'secondary' :
                  event.type === 'terminal_output' ? 'warning' :
                  'warning'
                }
              />
              <Typography variant="caption" color="text.secondary">
                {formatTimestamp(event.timestamp)}
              </Typography>
            </Box>
            
            {event.type === 'interaction' && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Question Type:</strong> {event.question_type}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Student Intent:</strong> {event.student_intent}
                </Typography>
                
                {/* Student Facing Description */}
                {event.student_facing_description && (
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, borderLeft: '3px solid #1976d2' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1565c0', display: 'block', mb: 0.5 }}>
                      💭 What the student was trying to do:
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#555' }}>
                      "{event.student_facing_description}"
                    </Typography>
                  </Box>
                )}

                {event.student_reported_intent && (
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, borderLeft: '3px solid #4caf50' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#2e7d32', display: 'block', mb: 0.5 }}>
                      💬 Why the student asked for help (in their own words):
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#555' }}>
                      "{event.student_reported_intent}"
                    </Typography>
                  </Box>
                )}
                
                {/* Rating Display */}
                {event.intent_rating && (
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: '#fff8e1', borderRadius: 1, borderLeft: '3px solid #ffd54f' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#f57f17' }}>
                        ⭐ Student Rating:
                      </Typography>
                      <Rating value={event.intent_rating} size="small" readOnly />
                      <Typography variant="caption" color="text.secondary">
                        ({event.intent_rating}/5)
                      </Typography>
                    </Box>
                    {event.rating_timestamp && (
                      <Typography variant="caption" color="text.secondary">
                        Rated: {formatTimestamp(event.rating_timestamp)}
                      </Typography>
                    )}
                  </Box>
                )}
                
                {event.message_history && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="caption">Message History:</Typography>
                    {event.message_history.map((msg, i) => (
                      <Typography key={i} variant="body2" sx={{ fontSize: '0.85rem', mt: 0.5 }}>
                        <strong>{msg.role === 'user' ? '👤 Student' : '🤖 AI'}:</strong> {msg.content?.substring(0, 100)}...
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
            
            {event.type === 'message' && (
              <Box>
                <Typography variant="body2">
                  <strong>{event.sender_id === -4 ? '🤖 AI' : '👤 Student'}:</strong> {event.content}
                </Typography>
              </Box>
            )}
            
            {event.type === 'test_run' && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Test Execution</strong>
                </Typography>
                {event.result_list && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {event.result_list.map((result, i) => (
                      <Chip 
                        key={i}
                        label={`Test ${i + 1}: ${result ? '✓ Pass' : '✗ Fail'}`}
                        size="small"
                        color={result ? 'success' : 'error'}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
                {event.error && Object.keys(event.error).length > 0 && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: '#ffebee', borderRadius: 1 }}>
                    <Typography variant="caption" color="error">Error:</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
                      {JSON.stringify(event.error)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            
            {event.type === 'code_submission' && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Code Written</strong> ({event.passrate != null ? `Pass Rate: ${event.passrate}%` : 'not tested'})
                </Typography>
                <Box sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {event.content?.substring(0, 200)}...
                </Box>
              </Box>
            )}

            {event.type === 'terminal_output' && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Terminal Output</strong>
                </Typography>
                <Box sx={{ 
                  mt: 1, 
                  p: 2, 
                  bgcolor: event.text?.includes('Error') || event.text?.includes('error') || event.text?.includes('Traceback') ? '#ffebee' : '#f5f5f5', 
                  borderRadius: 1, 
                  fontFamily: 'monospace', 
                  fontSize: '0.85rem',
                  whiteSpace: 'pre-wrap',
                  border: event.text?.includes('Successfully Ran') ? '1px solid #4caf50' : '1px solid #e0e0e0'
                }}>
                  {event.text}
                </Box>
              </Box>
            )}
          </Paper>
        ))
      )}
    </Box>
  );

  // The old interaction/message_history/intent_rating fields this used to read came from a
  // specific past study's LLM-intent-classification instrumentation (question_type,
  // student_intent, intent_rating) - that pipeline stopped being written on 2026-08-21/22
  // when the app moved to batched telemetry_events, and has no current equivalent (see
  // RawDataPanel.jsx, which is why studentData.interactions is always []). This tab used to
  // silently show "No LLM interactions recorded" for every student regardless of how much
  // they actually used the AI, even though the same telemetry (CHAT_QUERY/CHAT_RESPONSE)
  // that powers the Timeline tab is already available as studentData.messages - render that
  // real transcript instead of the permanently-empty intent-classification view.
  const renderInteractionsView = () => (
    <Box sx={{ height: '600px', overflowY: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        LLM Interactions ({studentData.messages?.length || 0})
      </Typography>

      {studentData.messages?.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No LLM interactions recorded for this student yet.
        </Typography>
      ) : (
        studentData.messages?.map((message, index) => (
          <Paper
            key={index}
            sx={{
              p: 2,
              mb: 1,
              bgcolor: message.sender_id === -4 ? '#e3f2fd' : '#f5f5f5'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">
                {message.sender_id === -4 ? '🤖 AI Assistant' : '👤 Student'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatTimestamp(message.created_time || message.timestamp)}
              </Typography>
            </Box>
            <Typography variant="body2">{message.content}</Typography>
          </Paper>
        ))
      )}
    </Box>
  );

  const renderRatingsView = () => {
    const ratedInteractions = studentData.interactions?.filter(i => i.intent_rating) || [];
    const totalInteractions = studentData.interactions?.length || 0;
    const ratingPercentage = totalInteractions > 0 ? (ratedInteractions.length / totalInteractions * 100).toFixed(1) : 0;
    
    // Calculate rating distribution
    const ratingCounts = [0, 0, 0, 0, 0]; // 1-5 stars
    ratedInteractions.forEach(i => {
      if (i.intent_rating >= 1 && i.intent_rating <= 5) {
        ratingCounts[i.intent_rating - 1]++;
      }
    });
    
    const averageRating = ratedInteractions.length > 0
      ? (ratedInteractions.reduce((sum, i) => sum + i.intent_rating, 0) / ratedInteractions.length).toFixed(2)
      : 'N/A';

    return (
      <Box sx={{ height: '600px', overflowY: 'auto' }}>
        <Typography variant="h6" gutterBottom>
          Classification Rating Analysis
        </Typography>

        {/* Individual Ratings */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Rated Interactions ({ratedInteractions.length})
        </Typography>
        {ratedInteractions.map((interaction, index) => {
        return (
          <Paper key={index} sx={{ p: 2, mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Chip label={interaction.question_type} size="small" color="primary" />
                  <Chip label={interaction.student_intent} size="small" color="secondary" />
                </Box>
                
                {/* Student's Self-Reported Intent */}
                {interaction.student_reported_intent && (
                  <Box sx={{ mb: 1, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, borderLeft: '3px solid #4caf50' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#2e7d32', display: 'block', mb: 0.5 }}>
                      💭 Why the student asked for help:
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#555' }}>
                      "{interaction.student_reported_intent}"
                    </Typography>
                  </Box>
                )}
                
                <Typography variant="caption" color="text.secondary">
                  Asked: {formatTimestamp(interaction.created_time)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Rating value={interaction.intent_rating} size="small" readOnly />
                <Typography variant="caption" color="text.secondary" display="block">
                  {interaction.intent_rating}/5
                </Typography>
                {interaction.rating_timestamp && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Rated: {formatTimestamp(interaction.rating_timestamp)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        );
      })}
      </Box>
    );
  };

  const renderMessagesView = () => (
    <Box sx={{ height: '600px', overflowY: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Chat Messages ({studentData.messages?.length || 0})
      </Typography>
      
      {studentData.messages?.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No chat messages for this student yet.
        </Typography>
      ) : (
        studentData.messages?.map((message, index) => (
          <Paper 
            key={index} 
            sx={{ 
              p: 2, 
              mb: 1,
              bgcolor: message.sender_id === -4 ? '#e3f2fd' : '#f5f5f5'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">
                {message.sender_id === -4 ? '🤖 AI Assistant' : '👤 Student'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatTimestamp(message.created_time || message.timestamp)}
              </Typography>
            </Box>
            <Typography variant="body2">{message.content}</Typography>
            
            {/* Add Keystroke Data */}
            {message.keystrokes && message.keystrokes.length > 0 && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1, borderLeft: '3px solid #ff9800' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#e65100' }}>
                  ⌨️ Typing Activity ({message.keystrokes.length} keystrokes)
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Average typing speed: {calculateTypingSpeed(message.keystrokes)} ms/keystroke
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Longest pause: {findLongestPause(message.keystrokes)} ms
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        ))
      )}
    </Box>
  );

  const calculateTypingSpeed = (keystrokes) => {
    if (!keystrokes || keystrokes.length < 2) return 'N/A';
    const totalTime = keystrokes.reduce((sum, k) => sum + (k.timeDifference || 0), 0);
    return Math.round(totalTime / keystrokes.length);
  };

  const findLongestPause = (keystrokes) => {
    if (!keystrokes || keystrokes.length === 0) return 'N/A';
    return Math.max(...keystrokes.map(k => k.timeDifference || 0));
  };

  const renderCodeSubmissionsView = () => (
    <Box sx={{ height: '600px', overflowY: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Code Submissions ({studentData.codeSubmissions?.length || 0})
      </Typography>
      
      {studentData.codeSubmissions?.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No code submissions for this student yet.
        </Typography>
      ) : (
        studentData.codeSubmissions?.map((code, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {formatTimestamp(code.created_time)}
              </Typography>
              <Chip
                label={code.passrate != null ? `Pass Rate: ${code.passrate}%` : 'Not tested'}
                size="small"
                color={code.passrate == null ? 'default' : code.passrate >= 80 ? 'success' : code.passrate >= 50 ? 'warning' : 'error'}
              />
            </Box>
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {code.content}
              </pre>
            </Box>
          </Paper>
        ))
      )}
    </Box>
  );

  const renderTestRunsView = () => (
    <Box sx={{ height: '600px', overflowY: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Test Runs ({studentData.testRuns?.length || 0})
      </Typography>
      
      {studentData.testRuns?.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No test runs for this student yet.
        </Typography>
      ) : (
        studentData.testRuns?.map((testRun, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {formatTimestamp(testRun.created_time)}
            </Typography>
            
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Test Results:</Typography>
            {testRun.result_list && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {testRun.result_list.map((result, i) => (
                  <Chip 
                    key={i}
                    label={`Test ${i + 1}: ${result ? '✓ Pass' : '✗ Fail'}`}
                    size="small"
                    color={result ? 'success' : 'error'}
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
            
            {testRun.error && (
              <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="error" gutterBottom>Error:</Typography>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(testRun.error, null, 2)}
                  </pre>
                </Box>
              </Box>
            )}
          </Paper>
        ))
      )}
    </Box>
  );

  const renderTypingActivityView = () => {
    // studentData.typingActivity is reconstructed (see RawDataPanel.jsx) from the real
    // per-keystroke CODE_TYPE/CODE_DELETE/CHAT_TYPE telemetry, one item per code file plus
    // one for chat - not per message/submission, which is what this used to (and no longer
    // does) filter on.
    const allTypingActivity = (studentData.typingActivity || [])
      .map(item => ({ ...item, timestamp: new Date(item.created_time) }))
      .sort((a, b) => b.timestamp - a.timestamp);

    const calculateTypingSpeed = (keystrokes) => {
      if (!keystrokes || !Array.isArray(keystrokes) || keystrokes.length < 2) return 0;
      const totalTime = keystrokes.reduce((sum, k) => sum + (k?.timeDifference || 0), 0);
      return Math.round(totalTime / keystrokes.length);
    };

    const findLongestPause = (keystrokes) => {
      if (!keystrokes || !Array.isArray(keystrokes) || keystrokes.length === 0) return 0;
      return Math.max(...keystrokes.map(k => k?.timeDifference || 0));
    };

    const getTotalTypingTime = (keystrokes) => {
      if (!keystrokes || !Array.isArray(keystrokes) || keystrokes.length === 0) return 0;
      return keystrokes.reduce((sum, k) => sum + (k?.timeDifference || 0), 0);
    };

    const getKeystrokePattern = (keystrokes) => {
      if (!keystrokes || !Array.isArray(keystrokes)) {
        return { backspaceCount: 0, deleteCount: 0, totalEdits: 0, editRate: '0.0' };
      }
      const backspaceCount = keystrokes.filter(k => k?.key === 'Backspace').length;
      const deleteCount = keystrokes.filter(k => k?.key === 'Delete').length;
      const totalEdits = backspaceCount + deleteCount;
      const editRate = keystrokes.length > 0 ? (totalEdits / keystrokes.length * 100).toFixed(1) : '0.0';
      
      return { backspaceCount, deleteCount, totalEdits, editRate };
    };

    return (
      <Box sx={{ height: '600px', overflowY: 'auto' }}>
        <Typography variant="h6" gutterBottom>
          Typing Activity Analysis
        </Typography>
        
        {allTypingActivity.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No typing activity recorded for this student yet.
          </Typography>
        ) : (
          <>
            {/* Individual Activity Details */}
            {allTypingActivity.map((item, index) => {
              if (!item.keystrokes || !Array.isArray(item.keystrokes)) return null;
              
              const typingSpeed = calculateTypingSpeed(item.keystrokes);
              const longestPause = findLongestPause(item.keystrokes);
              const totalTime = getTotalTypingTime(item.keystrokes);
              const pattern = getKeystrokePattern(item.keystrokes);

              return (
                <Paper 
                  key={index} 
                  sx={{ p: 2, mb: 2 }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.type === 'chat' ? '💬 Chat' : `💻 Code (${item.label})`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • {item.keystrokes.length} keystrokes
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(item.timestamp)}
                    </Typography>
                  </Box>

                  {/* Content Preview - reconstructed by replaying each keystroke's typed
                      text in order; approximate (doesn't account for cursor repositioning
                      between edits), but gives a real sense of what was typed. */}
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {item.keystrokes.filter(k => k.key !== 'Backspace').map(k => k.key).join('').slice(0, 300)}
                      </pre>
                    </Box>
                  </Box>

                  {/* Typing Metrics */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Keystrokes</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{item.keystrokes.length}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Avg Speed</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{typingSpeed} ms</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Longest Pause</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{(longestPause / 1000).toFixed(1)}s</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Total Time</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{(totalTime / 1000).toFixed(1)}s</Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Editing Pattern */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Editing Pattern
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Typography variant="body2">
                        <strong>Backspaces:</strong> {pattern.backspaceCount}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Deletes:</strong> {pattern.deleteCount}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Edit Rate:</strong> {pattern.editRate}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* Keystroke Timeline Visualization */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Typing Timeline
                    </Typography>
                    <Box sx={{ 
                      p: 1.5, 
                      bgcolor: '#f5f5f5', 
                      borderRadius: 1, 
                      maxHeight: '100px', 
                      overflowY: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      border: '1px solid #e0e0e0'
                    }}>
                      {item.keystrokes.map((k, i) => (
                        <span 
                          key={i} 
                          style={{ 
                            color: k?.key === 'Backspace' || k?.key === 'Delete' ? '#666' : 
                                  (k?.timeDifference || 0) > 1000 ? '#999' : '#333',
                            marginRight: '4px'
                          }}
                          title={`${k?.key || ''} (+${k?.timeDifference || 0}ms)`}
                        >
                          {k?.key === 'Backspace' ? '⌫' : 
                          k?.key === 'Delete' ? '⌦' : 
                          k?.key === 'Enter' ? '↵' : 
                          k?.key === ' ' ? '␣' : 
                          k?.key?.length === 1 ? k.key : '·'}
                        </span>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '600px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Avatar
          sx={{
            width: 60,
            height: 60,
            mr: 2,
            bgcolor: getStudentColor(students.indexOf(student))
          }}
        >
          {getStudentInitials(student)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5">
            {student.first_name} {student.last_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {student.email || 'Guest User'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ContentCopy />}
            onClick={handleCopyTimelineOnly}
          >
            Copy Timeline
          </Button>
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={handleCopyAllData}
            title="Includes mouse tracking & keystrokes (slower)"
          >
            Full Export
          </Button>
        </Box>
      </Box>

      <Tabs value={detailTab} onChange={(e, v) => setDetailTab(v)} sx={{ mb: 2 }}>
        <Tab label="Timeline" icon={<Assessment />} iconPosition="start" />
        <Tab label="LLM Interactions" icon={<Chat />} iconPosition="start" />
        <Tab label="Ratings" icon={<Star />} iconPosition="start" />
        <Tab label="Messages" icon={<Chat />} iconPosition="start" />
        <Tab label="Code" icon={<Code />} iconPosition="start" />
        <Tab label="Test Runs" icon={<PlayArrow />} iconPosition="start" />
        <Tab label="Typing Activity" icon={<span>⌨️</span>} iconPosition="start" />
        <Tab label="Session Replay" icon={<span>🎥</span>} iconPosition="start" />
      </Tabs>

      {detailTab === 0 && renderChronologicalView()}
      {detailTab === 1 && renderInteractionsView()}
      {detailTab === 2 && renderRatingsView()}
      {detailTab === 3 && renderMessagesView()}
      {detailTab === 4 && renderCodeSubmissionsView()}
      {detailTab === 5 && renderTestRunsView()}
      {detailTab === 6 && renderTypingActivityView()}
      {detailTab === 7 && <TelemetryReplayView events={studentData.rawEvents} isGuiSession={isGuiSession} guiConfig={guiConfig} replayOptions={replayOptions} />} 

      {/* Success Toast */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCopySuccess(false)} severity="success" sx={{ width: '100%' }}>
          {copyMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentDetailView;